import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { CollectionFieldPath, FieldSelection, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useFieldSelectionContexts } from '#layers/blocks/public';
import { idFieldSelectionToFieldSelection, mergeFieldSelections, resolveFieldPathSelection, toCollectionFieldPathKey } from '#layers/data-model/public';

export function useTableQuerySelection(
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>,
	visibleColumnPaths: MaybeRefOrGetter<CollectionFieldPath[]>,
): ComputedRef<FieldSelection> {
	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	// Cache each path's field selection per collection so column edits only resolve the delta. The query
	// schema decides how relations are previewed, so a new one starts a new cache.
	const cache = computed(() => {
		fieldSelectionCtx.getQuerySchema();
		return new WeakMap<ResolvedMonospaceCollection, Map<string, FieldSelection>>();
	});

	return computed<FieldSelection>(() => {
		const col = toValue(collection);
		const paths = toValue(visibleColumnPaths);

		if (!col || paths.length === 0) return { fields: ['*'] };

		let perCollection = cache.value.get(col);

		if (!perCollection) {
			perCollection = new Map<string, FieldSelection>();
			cache.value.set(col, perCollection);
		}

		const perPathSelections: FieldSelection[] = [];

		for (const fieldPath of paths) {
			const pathKey = toCollectionFieldPathKey(fieldPath);
			let fieldSelection = perCollection.get(pathKey);

			if (!fieldSelection) {
				const idSelection = resolveFieldPathSelection(col, fieldPath as unknown as GlobalFieldId[], fieldSelectionCtx);
				fieldSelection = idFieldSelectionToFieldSelection(idSelection, idCtx);

				perCollection.set(pathKey, fieldSelection);
			}

			perPathSelections.push(fieldSelection);
		}

		return mergeFieldSelections(...perPathSelections);
	});
}
